/**
 * Item 2 (Jun-2026, User-Wunsch) — Mitarbeiter-Zeiterfassung im Dashboard.
 * Jun-2026 (User-Wunsch v2): Einklappbare Leiste — die eingestempelte Zeit ist
 * IMMER sichtbar (volle Breite); Klick auf die Leiste klappt Konten/Abwesenheiten
 * auf. NEU: Pausenfunktion (Frühstück/Mittag/Zigarette), Tageslimit 1 Stunde,
 * Hinweis wenn Sollarbeitszeit nicht erreicht ist.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Clock, Play, Square, CalendarDays, Plane, GraduationCap, Scale, Activity, Loader2, Plus, Coffee, ChevronDown, Cigarette, UtensilsCrossed, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

function fmtHM(hours) {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtElapsed(secs) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const KIND_META = {
    urlaub: { label: "Urlaub", icon: Plane },
    schule: { label: "Berufsschule", icon: GraduationCap },
    ausgleich: { label: "Ausgleichstag", icon: Scale },
    krank: { label: "Krank", icon: CalendarDays },
};

const BREAK_META = {
    fruehstueck: { label: "Frühstückspause", icon: Coffee },
    mittag: { label: "Mittagspause", icon: UtensilsCrossed },
    zigarette: { label: "Zigarettenpause", icon: Cigarette },
};

export default function EmployeeTimeCard({ employerId }) {
    const cfg = employerId ? { headers: { "X-Employer-Id": employerId } } : undefined;
    const [status, setStatus] = useState(null);
    const [summary, setSummary] = useState(null);
    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [breakElapsed, setBreakElapsed] = useState(0);
    const [showForm, setShowForm] = useState(false);
    const [showBreakMenu, setShowBreakMenu] = useState(false);
    const [expanded, setExpanded] = useState(() => localStorage.getItem("tw_timecard_open") === "1");
    const [form, setForm] = useState({ kind: "urlaub", date_from: "", date_to: "", note: "", worked_saturday: "" });
    const [eligibleSat, setEligibleSat] = useState([]);
    const tick = useRef(null);

    const load = useCallback(async () => {
        const [st, su, ab, el] = await Promise.all([
            api.get("/me/time/status", cfg).catch(() => ({ data: null })),
            api.get("/me/time/summary", cfg).catch(() => ({ data: null })),
            api.get(`/me/time/absences?year=${new Date().getFullYear()}`, cfg).catch(() => ({ data: { items: [] } })),
            api.get("/me/time/ausgleich-eligible", cfg).catch(() => ({ data: { items: [] } })),
        ]);
        setStatus(st.data);
        setSummary(su.data);
        setAbsences(ab.data?.items || []);
        setEligibleSat(el.data?.items || []);
        setLoading(false);
    }, [employerId]);

    useEffect(() => { load(); }, [load]);

    // Jul-2026 (User-Wunsch) — Profilfoto-Banner bleibt sichtbar, BIS der
    // Mitarbeiter ein Foto hochgeladen hat (kein Verschieben/Erinnern möglich).
    // Klick auf den Banner öffnet direkt die Foto-Auswahl (Selbst-Upload).
    const [photoReminder, setPhotoReminder] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const photoInputRef = useRef(null);
    useEffect(() => {
        (async () => {
            try {
                const { data: me } = await api.get("/auth/me");
                if (me?.role !== "zweiradverkaeufer" || me?.profile_photo_url) return;
                setPhotoReminder(true);
            } catch { /* optional */ }
        })();
    }, []);
    const uploadProfilePhoto = async (file) => {
        if (!file) return;
        setPhotoBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/users/me/profile-photo", { profile_photo_url: up.url });
            setPhotoReminder(false);
            toast.success("Profilfoto gespeichert — danke!");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Foto-Upload fehlgeschlagen — bitte erneut versuchen.");
        } finally {
            setPhotoBusy(false);
            if (photoInputRef.current) photoInputRef.current.value = "";
        }
    };

    // Live-Timer, solange eingestempelt — während einer Pause friert die Arbeitszeit ein.
    useEffect(() => {
        if (tick.current) clearInterval(tick.current);
        if (status?.clocked_in && status?.open_since) {
            const start = new Date(status.open_since).getTime();
            const base = (status.today_seconds || 0) - Math.max(0, (Date.now() - start) / 1000);
            const breakStart = status?.on_break && status?.break_since ? new Date(status.break_since).getTime() : null;
            const update = () => {
                let secs = base + (Date.now() - start) / 1000;
                if (breakStart) {
                    const bs = Math.max(0, (Date.now() - breakStart) / 1000);
                    secs -= bs;
                    setBreakElapsed(bs);
                }
                setElapsed(Math.max(0, secs));
            };
            update();
            tick.current = setInterval(update, 1000);
        } else {
            setElapsed(status?.today_seconds || 0);
            setBreakElapsed(0);
        }
        return () => { if (tick.current) clearInterval(tick.current); };
    }, [status]);

    const toggleExpanded = () => {
        setExpanded((e) => {
            try { localStorage.setItem("tw_timecard_open", e ? "0" : "1"); } catch { /* noop */ }
            return !e;
        });
    };

    const punch = async (action) => {
        setBusy(true);
        try {
            const r = await api.post(`/me/time/${action}`, action === "clock-in" ? { source: "mobile" } : {}, cfg);
            if (action === "clock-out") {
                if (r.data?.target_reached === false) {
                    toast.warning(`Hinweis: Tagessoll noch nicht erreicht — es fehlen ${fmtHM(r.data.missing_hours || 0)} (Pausen zählen nicht als Arbeitszeit).`, { duration: 8000 });
                } else {
                    toast.success("Ausgestempelt — Tagessoll erreicht. Feierabend!");
                }
            } else {
                toast.success("Eingestempelt");
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const startBreak = async (kind) => {
        setBusy(true);
        setShowBreakMenu(false);
        try {
            const r = await api.post("/me/time/break-start", { kind }, cfg);
            toast.success(`${BREAK_META[kind]?.label || "Pause"} gestartet — noch ${r.data?.remaining_minutes ?? "?"} Min. Pausenbudget heute.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Pause konnte nicht gestartet werden");
        } finally { setBusy(false); }
    };

    const endBreak = async () => {
        setBusy(true);
        try {
            const r = await api.post("/me/time/break-end", {}, cfg);
            if (r.data?.warning) toast.warning(r.data.warning, { duration: 9000 });
            else toast.success(`Pause beendet (${r.data?.minutes ?? "?"} Min.) — heute ${Math.round(r.data?.today_break_minutes || 0)} / 60 Min. Pause.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Pause konnte nicht beendet werden");
        } finally { setBusy(false); }
    };

    const submitAbsence = async () => {
        if (!form.date_from) { toast.error("Startdatum wählen"); return; }
        if (form.kind === "ausgleich" && !form.worked_saturday) { toast.error("Bitte den gearbeiteten Samstag wählen"); return; }
        setBusy(true);
        try {
            await api.post("/me/time/absence", form, cfg);
            toast.success("Abwesenheit beantragt");
            setShowForm(false);
            setForm({ kind: "urlaub", date_from: "", date_to: "", note: "", worked_saturday: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Antrag fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <div className="py-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Lädt …</div>;

    const clockedIn = status?.clocked_in;
    const onBreak = status?.on_break;
    const breakUsed = Math.round(status?.today_break_minutes || 0) + (onBreak ? Math.round(breakElapsed / 60) : 0);
    const breakLimit = status?.break_limit_minutes || 60;
    const barColor = onBreak ? "bg-amber-500 border-amber-600" : clockedIn ? "bg-emerald-600 border-emerald-700" : "bg-[#111827] border-[#111827]";

    return (
        <div data-testid="employee-time-card">
            {status?.reminder && (
                <div className="mb-3 rounded-sm border-2 border-red-400 bg-red-50 px-4 py-3 flex flex-wrap items-center gap-3 animate-pulse" data-testid="time-reminder-banner">
                    <Clock className="w-5 h-5 text-red-600 shrink-0" />
                    <p className="flex-1 min-w-[200px] text-sm font-bold text-red-800">
                        {status.reminder === "clock_in"
                            ? "Du bist heute eingeplant, aber noch nicht eingestempelt — bitte jetzt einstempeln!"
                            : "Deine Arbeitszeit läuft noch — bitte nicht vergessen auszustempeln!"}
                    </p>
                    <Button size="sm" onClick={() => punch(status.reminder === "clock_in" ? "clock-in" : "clock-out")} disabled={busy}
                        className="h-8 bg-red-600 hover:bg-red-700 text-white font-bold shrink-0" data-testid="time-reminder-action">
                        {status.reminder === "clock_in" ? <><Play className="w-3.5 h-3.5 mr-1" /> Jetzt einstempeln</> : <><Square className="w-3.5 h-3.5 mr-1" /> Jetzt ausstempeln</>}
                    </Button>
                </div>
            )}
            {photoReminder && (
                <div
                    className={`mb-3 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-start gap-3 cursor-pointer hover:bg-amber-100 transition-colors ${photoBusy ? "opacity-60 cursor-wait" : ""}`}
                    onClick={() => { if (!photoBusy) photoInputRef.current?.click(); }}
                    role="button"
                    title="Klicken und Profilfoto auswählen"
                    data-testid="photo-reminder-banner"
                >
                    {photoBusy ? <Loader2 className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 animate-spin" /> : <Camera className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-[220px]">
                        <p className="text-sm font-bold text-gray-800">Profilfoto fehlt noch — jetzt hochladen</p>
                        <p className="text-xs text-gray-600 mt-0.5">
                            Klicke hier und wähle ein Foto aus — es erscheint als dein Avatar und wenn du als
                            Ansprechpartner hinterlegt bist. Dieser Hinweis bleibt sichtbar, bis ein Foto hinterlegt ist.
                        </p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] text-white rounded-sm px-3 py-1.5" data-testid="photo-reminder-upload-btn">
                        <Camera className="w-3.5 h-3.5" /> Foto auswählen
                    </span>
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => uploadProfilePhoto(e.target.files?.[0])}
                        data-testid="photo-reminder-file-input"
                    />
                </div>
            )}
            {/* Immer sichtbare Stempel-Leiste (volle Breite) — Klick klappt Details auf */}
            <div
                className={`rounded-sm border text-white px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 cursor-pointer select-none transition-colors ${barColor}`}
                onClick={toggleExpanded}
                data-testid="time-clock"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Clock className="w-5 h-5 opacity-80 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-2xl sm:text-3xl font-black font-mono-num leading-none" data-testid="time-clock-elapsed">{fmtElapsed(elapsed)}</div>
                        <div className="text-[10px] opacity-80 truncate" data-testid="time-clock-subline">
                            {onBreak
                                ? `${BREAK_META[status?.break_kind]?.label || "Pause"} seit ${fmtElapsed(breakElapsed)}`
                                : clockedIn && status?.open_since
                                    ? `Eingestempelt seit ${new Date(status.open_since).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`
                                    : `Heute: ${fmtHM(status?.today_hours || 0)} / ${status?.daily_target_hours || 8}h Soll`}
                        </div>
                    </div>
                </div>
                {clockedIn && (
                    <div className="text-[10px] opacity-80 font-bold" data-testid="time-break-budget">
                        Pausen heute: {breakUsed} / {breakLimit} Min.
                    </div>
                )}
                <div className="flex items-center gap-2 ml-auto" onClick={(e) => e.stopPropagation()}>
                    {!clockedIn && (
                        <Button size="sm" onClick={() => punch("clock-in")} disabled={busy} className="h-8 bg-[#FFCC00] text-[#111827] hover:bg-[#e6b800] font-bold" data-testid="time-clock-in-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1.5" /> Einstempeln</>}
                        </Button>
                    )}
                    {clockedIn && onBreak && (
                        <Button size="sm" onClick={endBreak} disabled={busy} className="h-8 bg-white text-amber-700 hover:bg-gray-100 font-bold" data-testid="time-break-end-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1.5" /> Pause beenden</>}
                        </Button>
                    )}
                    {clockedIn && !onBreak && (
                        <div className="relative">
                            <Button size="sm" variant="outline" onClick={() => setShowBreakMenu((s) => !s)} disabled={busy} className="h-8 bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white font-bold" data-testid="time-break-menu-btn">
                                <Coffee className="w-3.5 h-3.5 mr-1.5" /> Pause
                            </Button>
                            {showBreakMenu && (
                                <div className="absolute right-0 top-9 z-20 bg-white border border-gray-200 rounded-sm shadow-lg py-1 w-48" data-testid="time-break-menu">
                                    {Object.entries(BREAK_META).map(([k, m]) => {
                                        const Icon = m.icon;
                                        return (
                                            <button key={k} type="button" onClick={() => startBreak(k)}
                                                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#111827] hover:bg-gray-100 text-left"
                                                data-testid={`time-break-start-${k}`}>
                                                <Icon className="w-3.5 h-3.5 text-gray-500" /> {m.label}
                                            </button>
                                        );
                                    })}
                                    <div className="px-3 py-1.5 text-[9px] text-gray-400 border-t border-gray-100">Max. 60 Min. Pause pro Tag</div>
                                </div>
                            )}
                        </div>
                    )}
                    {clockedIn && !onBreak && (
                        <Button size="sm" onClick={() => punch("clock-out")} disabled={busy} className="h-8 bg-white text-emerald-700 hover:bg-gray-100 font-bold" data-testid="time-clock-out-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Square className="w-3.5 h-3.5 mr-1.5" /> Ausstempeln</>}
                        </Button>
                    )}
                    <ChevronDown className={`w-4 h-4 opacity-70 transition-transform ${expanded ? "rotate-180" : ""}`} data-testid="time-clock-chevron" />
                </div>
            </div>

            {/* Aufklappbarer Bereich: Konten + Abwesenheiten */}
            {expanded && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3" data-testid="time-card-details">
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="time-balances">
                        <h3 className="font-display font-bold text-sm text-[#111827] mb-3">Meine Konten</h3>
                        <div className="space-y-2.5 text-sm">
                            <Row label="Diese Woche" value={`${fmtHM(summary?.week_hours || 0)} / ${summary?.week_target || 40}h`} testid="time-week" />
                            <Row label="Dieser Monat" value={`${fmtHM(summary?.month_hours || 0)} / ${fmtHM(summary?.month_target_hours || 0)} Soll`} testid="time-month" />
                            <Row label="Überstunden (Monat)" value={`${(summary?.overtime_hours ?? 0) >= 0 ? "+" : ""}${fmtHM(Math.abs(summary?.overtime_hours || 0))}`} highlight testid="time-overtime" />
                            <div className="text-[10px] text-gray-400 -mt-1.5 pl-0.5" data-testid="time-overtime-detail">
                                davon {fmtHM(summary?.overtime_agreed_hours || 0)} vereinbart (kein Ausgleich) · {fmtHM(summary?.overtime_compensable_hours || 0)} ausgleichsfähig
                            </div>
                            <Row label="Resturlaub" value={`${summary?.vacation_remaining ?? 0} / ${summary?.vacation_total ?? 0} Tage`} testid="time-vacation-remaining" />
                            <Row label="Ausgleichstage" value={`${summary?.ausgleich_available_count ?? 0} verfügbar`} highlight testid="time-ausgleich-remaining" />
                            <Row label="Krankheitstage" value={`${summary?.krank_days ?? 0} Tage`} testid="time-krank" />
                            <Row label="Berufsschultage" value={`${summary?.schule_days ?? 0}`} testid="time-schule" />
                            <div className="flex items-center gap-1.5 pt-1 text-[10px] text-gray-400">
                                <Activity className="w-3 h-3" /> {status?.activities_today || 0} System-Aktivitäten heute
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="time-absences">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-bold text-sm text-[#111827]">Abwesenheiten {new Date().getFullYear()}</h3>
                            <Button size="sm" variant="outline" onClick={() => setShowForm((s) => !s)} className="h-7 text-xs" data-testid="time-absence-toggle">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Antrag
                            </Button>
                        </div>
                        {showForm && (
                            <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5 mb-3 space-y-2" data-testid="time-absence-form">
                                <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="w-full h-8 text-xs border border-gray-300 rounded-sm px-1.5 bg-white" data-testid="time-absence-kind">
                                    {Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                                </select>
                                {form.kind === "ausgleich" && (
                                    <select value={form.worked_saturday} onChange={(e) => setForm((f) => ({ ...f, worked_saturday: e.target.value }))} className="w-full h-8 text-xs border border-amber-300 bg-amber-50 rounded-sm px-1.5" data-testid="time-absence-saturday">
                                        <option value="">Gearbeiteten Samstag wählen …</option>
                                        {eligibleSat.map((s) => <option key={s.week_saturday} value={s.week_saturday}>{s.week_saturday} ({fmtHM(s.week_hours)}, {s.days_worked} Tage)</option>)}
                                    </select>
                                )}
                                {form.kind === "ausgleich" && eligibleSat.length === 0 && (
                                    <div className="text-[10px] text-amber-700" data-testid="time-absence-no-saturday">Kein Ausgleichsanspruch (mind. 6 Tage / 48 Std ohne Pausen).</div>
                                )}
                                <div className="flex gap-2">
                                    <Input type="date" value={form.date_from} onChange={(e) => setForm((f) => ({ ...f, date_from: e.target.value }))} className="h-8 text-xs" data-testid="time-absence-from" />
                                    <Input type="date" value={form.date_to} onChange={(e) => setForm((f) => ({ ...f, date_to: e.target.value }))} className="h-8 text-xs" data-testid="time-absence-to" />
                                </div>
                                <Input placeholder="Notiz (optional)" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="h-8 text-xs" data-testid="time-absence-note" />
                                <Button size="sm" onClick={submitAbsence} disabled={busy} className="w-full h-8 text-xs bg-[#111827] text-white" data-testid="time-absence-submit">Beantragen</Button>
                            </div>
                        )}
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {absences.length === 0 ? <div className="text-xs text-gray-400 py-2">Keine Einträge.</div> : absences.map((a) => {
                                const M = KIND_META[a.kind] || { label: a.kind, icon: CalendarDays };
                                const Icon = M.icon;
                                return (
                                    <div key={a.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50" data-testid={`time-absence-item-${a.id}`}>
                                        <span className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-gray-400" /> {M.label} <span className="text-gray-400">({a.days}T)</span></span>
                                        <span className="text-gray-500">{a.date_from === a.date_to ? a.date_from : `${a.date_from} – ${a.date_to}`}</span>
                                        <span className={`px-1.5 py-0.5 rounded-sm text-[9px] font-black uppercase ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.status === "approved" ? "OK" : a.status === "rejected" ? "Abgel." : "Offen"}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, highlight, testid }) {
    return (
        <div className="flex items-center justify-between" data-testid={testid}>
            <span className="text-gray-500">{label}</span>
            <span className={`font-bold font-mono-num ${highlight ? "text-indigo-700" : "text-[#111827]"}`}>{value}</span>
        </div>
    );
}
